import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useSocket } from '../../context/SocketContext';
import { API_BASE_URL } from '../../config/api';
import './MobileOrdersPage.css';

const MobileOrdersPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { on, off } = useSocket();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [noticeDismissed, setNoticeDismissed] = useState(false);

  const fetchOrders = useCallback(async (silent = false) => {
    if (!user?.id) { if (!silent) setLoading(false); return; }
    try {
      if (!silent) setLoading(true);
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE_URL}/orders?userId=${user.id}`, {
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      });
      if (res.ok) {
        const data = await res.json();
        setOrders(data.orders || data || []);
      }
    } catch (_) {
      setOrders([]);
    } finally {
      if (!silent) setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  useEffect(() => {
    if (!on || !off || !user?.id) return;
    const handleOrderChange = () => fetchOrders(true);
    on('order_updated', handleOrderChange);
    on('order_created', handleOrderChange);
    return () => {
      off('order_updated', handleOrderChange);
      off('order_created', handleOrderChange);
    };
  }, [on, off, user?.id, fetchOrders]);

  return (
    <div className="mo-page">
      {/* Header */}
      <div className="mo-header">
        <button className="mo-back-btn" onClick={() => navigate('/mobile/messenger')} aria-label="Back">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </button>
        <h1 className="mo-title">Orders</h1>
        <button className="mo-settings-btn" aria-label="Settings">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="3" />
            <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z" />
          </svg>
        </button>
      </div>

      {/* Push notification notice */}
      {!noticeDismissed && (
        <div className="mo-notice-bar">
          <div className="mo-notice-text">
            <strong>Stay in the know</strong>
            <span>Enable push notifications and never miss out on order updates and promotions. <button className="mo-enable-btn">Enable</button></span>
          </div>
          <button className="mo-notice-close" onClick={() => setNoticeDismissed(true)} aria-label="Dismiss">✕</button>
        </div>
      )}

      {/* Content */}
      <div className="mo-body">
        {loading ? (
          <div className="mo-loading">
            {[1, 2, 3].map(i => <div key={i} className="mo-skeleton" />)}
          </div>
        ) : orders.length === 0 ? (
          <div className="mo-empty">
            <div className="mo-empty-illustration">
              <svg viewBox="0 0 120 120" fill="none">
                <circle cx="60" cy="55" r="36" fill="#f5f5f5" />
                <ellipse cx="60" cy="95" rx="30" ry="8" fill="#f0f0f0" />
                {/* binoculars body */}
                <rect x="30" y="42" width="24" height="20" rx="6" fill="#ddd" />
                <rect x="66" y="42" width="24" height="20" rx="6" fill="#ddd" />
                <rect x="48" y="48" width="24" height="8" rx="3" fill="#ccc" />
                {/* lenses */}
                <circle cx="42" cy="52" r="10" fill="#e8e8e8" stroke="#ccc" strokeWidth="2" />
                <circle cx="78" cy="52" r="10" fill="#e8e8e8" stroke="#ccc" strokeWidth="2" />
                <circle cx="42" cy="52" r="5" fill="#d0d0d0" />
                <circle cx="78" cy="52" r="5" fill="#d0d0d0" />
                {/* person silhouette */}
                <circle cx="60" cy="78" r="6" fill="#ccc" />
                <path d="M46 95c0-8 6-14 14-14s14 6 14 14" fill="#ddd" />
                {/* orange blobs */}
                <circle cx="28" cy="70" r="8" fill="#ff6a00" opacity="0.7" />
                <ellipse cx="90" cy="68" rx="7" ry="10" fill="#ff6a00" opacity="0.7" />
              </svg>
            </div>
            <p className="mo-empty-label">No Items</p>
          </div>
        ) : (
          <div className="mo-order-list">
            {orders.map(order => (
              <div key={order.id} className="mo-order-card">
                <div className="mo-order-header">
                  <span className="mo-order-id">Order #{order.id || order.orderId}</span>
                  <span className={`mo-order-status mo-status-${(order.status || 'pending').toLowerCase()}`}>
                    {order.status || 'Pending'}
                  </span>
                </div>
                {order.items?.map((item, idx) => (
                  <div key={idx} className="mo-order-item">
                    <div className="mo-order-img">
                      {item.image ? (
                        <img src={item.image} alt={item.name} />
                      ) : (
                        <div className="mo-order-img-placeholder" />
                      )}
                    </div>
                    <div className="mo-order-info">
                      <p className="mo-order-name">{item.name || item.productName}</p>
                      <p className="mo-order-qty">Qty: {item.quantity}</p>
                    </div>
                    <p className="mo-order-price">${item.price}</p>
                  </div>
                ))}
                <div className="mo-order-footer">
                  <span className="mo-order-date">{order.createdAt ? new Date(order.createdAt).toLocaleDateString() : ''}</span>
                  <span className="mo-order-total">Total: ${order.totalAmount || order.total || 0}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MobileOrdersPage;
